import { ProviderType, type ReceiveTranscriptFragment, type SpeechRecognitionAction } from './types'

export type WebSpeechState =
  | {
      type: 'idle'
      provider: ProviderType.WebSpeech
    }
  | {
      type: 'session-ready'
      provider: ProviderType.WebSpeech
      recognition: SpeechRecognition
    }
  | {
      type: 'recording'
      provider: ProviderType.WebSpeech
      recognition: SpeechRecognition
      onTranscriptFragment: ReceiveTranscriptFragment
    }
  | {
      type: 'error'
      provider: ProviderType.WebSpeech
      error: string
    }

export function webSpeechTransitionToState(
  currentState: WebSpeechState,
  action: SpeechRecognitionAction,
  onError: (error: string) => void
): WebSpeechState {
  // Handle error - transition to error state from any state
  if (action.type === 'error-occurred') {
    return {
      type: 'error',
      provider: ProviderType.WebSpeech,
      error: action.error,
    }
  }

  // Handle reset - clean up all resources from any state
  if (action.type === 'reset') {
    if ('recognition' in currentState) {
      currentState.recognition.abort()
    }

    return {
      type: 'idle',
      provider: ProviderType.WebSpeech,
    }
  }

  // Handle start-session action
  if (action.type === 'start-session') {
    if (currentState.type !== 'idle') {
      // Invalid state transition - start-session only valid from idle state
      return {
        type: 'error',
        provider: ProviderType.WebSpeech,
        error: `Cannot transition from ${currentState.type} state to start-session`,
      }
    }

    const recognition =
      initializeGenericSpeechRecognition(globalThis.webkitSpeechRecognition) ||
      initializeGenericSpeechRecognition(globalThis.SpeechRecognition)

    if (!recognition) {
      return {
        type: 'error',
        provider: ProviderType.WebSpeech,
        error: 'Web Speech API is not supported in this browser',
      }
    }

    recognition.continuous = true
    recognition.interimResults = true

    recognition.onerror = event => {
      onError(`WebSpeech error: ${event.error}`)
    }

    return {
      type: 'session-ready',
      provider: ProviderType.WebSpeech,
      recognition,
    }
  }

  // Handle start-recording action
  if (action.type === 'start-recording') {
    if (currentState.type !== 'session-ready') {
      // Invalid state transition - start-recording only valid from session-ready state
      return {
        type: 'error',
        provider: ProviderType.WebSpeech,
        error: `Cannot transition from ${currentState.type} state to start-recording`,
      }
    }

    currentState.recognition.onresult = event => {
      // This logic processes the results so that they match the interface expected in ./types.ts ReceiveTranscriptFragment
      // For a given turn, we ALWAYS send back NON-FINAL results
      // For a given turn, we send back FINAL results the FIRST TIME and never after

      // Iterate over all turns that might have changed
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i]
        const { transcript } = result[0]

        action.onTranscriptFragment({
          turn: i,
          isFinal: result.isFinal,
          isFormatted: result.isFinal,
          content: transcript,
          // Since this API does not result word-granularity finality, mark them all as
          // the same isFinal value of the overall turn
          words: transcript
            .split(' ')
            .map((word: string) => ({ text: word, isFinal: result.isFinal })),
        })
      }
    }

    currentState.recognition.start()

    return {
      type: 'recording',
      provider: ProviderType.WebSpeech,
      recognition: currentState.recognition,
      onTranscriptFragment: action.onTranscriptFragment,
    }
  }

  // Handle stop-recording action
  if (action.type === 'stop-recording') {
    if (currentState.type !== 'recording') {
      // Invalid state transition - stop-recording only valid from recording state
      return {
        type: 'error',
        provider: ProviderType.WebSpeech,
        error: `Cannot transition from ${currentState.type} state to stop-recording`,
      }
    }

    currentState.recognition.stop()

    return {
      type: 'session-ready',
      provider: ProviderType.WebSpeech,
      recognition: currentState.recognition,
    }
  }

  // Assert all action types have been handled
  action satisfies never
  return currentState
}
export function initializeGenericSpeechRecognition(
  Constructor: typeof SpeechRecognition | typeof webkitSpeechRecognition
): SpeechRecognition | null {
  try {
    const recognition = new Constructor()
    return recognition
  } catch {
    return null
  }
}
